import axios from 'axios';
import { Endpoint } from 'types/url';
import { buildSearchParams } from 'utils/url';
import { GetParams, ScopeElement } from '../api.schema';
import { BaseHttpClient } from '../BaseHttpClient';
import { injectCommonInterceptors } from '../interceptors';
import {
  GetRuleResponse,
  GetRulesMetadataResponse,
  GetRulesResponse,
  CreateRuleRequest,
  UpdateRuleRequest,
  GetInsightsResponse,
  PromptToRuleRequest,
  PromptToRuleResponse,
  GetRelatedRulesResponse,
  RelatedRulesRequest,
  GetSkillsResponse,
} from './api.schema';
import { BASE_URL } from './consts';
import type {
  BulkFailure,
  BulkResponse,
  RequestSelection,
} from '../../../components/BulkActions';

export type BulkRuleOperation = 'activate' | 'deactivate' | 'set_scope';

/** Suggestions tab operations against the same /bulk endpoint (admin-gated). */
export type BulkSuggestionOperation = 'reject' | 'accept_activate';

/** Skills tab operations against the /skills/bulk endpoint (admin-gated). */
export type BulkSkillOperation = 'activate' | 'deactivate' | 'set_scope';

export type BulkOperation = BulkRuleOperation | BulkSuggestionOperation;

/** Params for the parameterized `set_scope` op: the scope to assign. */
export interface BulkRulesParams {
  scopes: string[];
  /** Authoritative when sent: the server derives `scopes` from them. */
  scopeElements?: ScopeElement[];
}

export interface BulkRulesRequest {
  operation: BulkOperation;
  selection: RequestSelection;
  params?: BulkRulesParams;
  dryRun?: boolean;
}

export interface BulkSkillsRequest {
  operation: BulkSkillOperation;
  selection: RequestSelection;
  params?: BulkRulesParams;
  dryRun?: boolean;
}

/** Aliases of the shared envelope, kept for the call sites that name it this way. */
export type BulkRulesFailure = BulkFailure;
export type BulkRulesResponse = BulkResponse;

const axiosRulesInstance = axios.create({
  baseURL: BASE_URL,
});

injectCommonInterceptors(axiosRulesInstance);

export class RulesClient extends BaseHttpClient {
  constructor() {
    super(axiosRulesInstance);
  }

  // Singleton instance for access without instantiation
  static override instance = new RulesClient();

  static async getRules(params?: GetParams) {
    const hasScopeFilter = Boolean(params?.scopes?.length);
    const searchParams = buildSearchParams({
      page: params?.page,
      pageSize: params?.pageSize,
      nameContains: params?.nameContains,
      sort: params?.sort,
      order: params?.order,
      state: params?.state,
      scopeMatch:
        params?.scopeMatch ?? (hasScopeFilter ? 'descendant' : undefined),
      include: params?.include,
      sourceUriContains: params?.sourceUriContains,
      excludeSkillRules: params?.excludeSkillRules,
    });

    const categories = ([] as string[]).concat(params?.category ?? []);

    params?.scopes?.forEach((s) => searchParams.append('scopes', s));
    categories.forEach((c) => searchParams.append('categories', c));
    params?.sourceTypes?.forEach((st) =>
      searchParams.append('sourceTypes', st),
    );

    return await RulesClient.get<GetRulesResponse>('/rules' as Endpoint, {
      params: searchParams,
    });
  }

  static async exportRulesAnalytics(): Promise<Blob> {
    return await RulesClient.get<Blob>(`/export`, {
      responseType: 'blob',
      extractNestedData: false,
    });
  }

  static async getRulesMetadata() {
    return await RulesClient.get<GetRulesMetadataResponse>('/metadata');
  }

  static async getRule(ruleId: string) {
    return await RulesClient.get<GetRuleResponse>(`/rule/${ruleId}`);
  }

  static async createRule(payload: CreateRuleRequest) {
    return await RulesClient.post<CreateRuleRequest>('/rule', payload);
  }

  static async updateRule(ruleId: string, payload: UpdateRuleRequest) {
    return await RulesClient.put<UpdateRuleRequest>(`/rule/${ruleId}`, payload);
  }

  static async deleteRule(ruleId: string) {
    return await RulesClient.delete<void>(`/rule/${ruleId}`);
  }

  static async bulkOperation(payload: BulkRulesRequest) {
    return await RulesClient.post<BulkRulesResponse>('/bulk', payload, {
      extractNestedData: false,
    });
  }

  static async bulkSkillsOperation(payload: BulkSkillsRequest) {
    return await RulesClient.post<BulkRulesResponse>('/skills/bulk', payload, {
      extractNestedData: false,
    });
  }

  static async getRulesInsights() {
    return await RulesClient.get<GetInsightsResponse>('/insights');
  }

  static async getRuleInsights(ruleId: string) {
    return await RulesClient.get<GetInsightsResponse>(
      `/insights?ruleId=${ruleId}`,
    );
  }

  static async promptToRule(payload: PromptToRuleRequest) {
    return await RulesClient.post<PromptToRuleResponse>(
      '/prompt-to-rule',
      payload,
    );
  }

  static async getRelatedRules({ rule }: RelatedRulesRequest) {
    return await RulesClient.post<GetRelatedRulesResponse>(
      '/rule_similarity',
      rule,
    );
  }

  static async getRelatedRulesByRuleId(ruleId: string) {
    return await RulesClient.get<GetRelatedRulesResponse>(
      `/rule/${ruleId}/rule-similarities`,
    );
  }

  static async getSkills(repos: string[]) {
    const params = new URLSearchParams();

    repos.forEach((r) => params.append('repos', r));
    params.append('ruleFields', 'name,source,state');

    return await RulesClient.get<GetSkillsResponse>(
      '/skills/by-repos' as Endpoint,
      { params },
    );
  }
}
